<script>
import { mapState, mapActions } from 'vuex'
import VueApexCharts from 'vue-apexcharts'

export default {
   components: {
      VueApexCharts,
   },
   data() {
      return {
         loading: false,
      }
   },
   computed: {
      ...mapState({
         statusResponse: state => state.DashboardLkpm.umkStatus,
         tahun: state => state.DashboardLkpm.tahun,
         refreshTimestamp: state => state.DashboardLkpm.refreshTimestamp,
         skin: state => state.appConfig.layout.skin,
      }),
      isDark() {
         return this.skin === 'dark'
      },
      // Urutkan ascending agar bar terbesar tampil paling atas
      // (ApexCharts menggambar kategori pertama di posisi bawah).
      sortedData() {
         return (this.statusResponse.data || [])
            .slice()
            .sort((a, b) => a.total - b.total)
      },
      // Palet gradasi modern — setiap batang mendapat pasangan warna berbeda.
      barPalette() {
         return [
            { from: '#7367f0', to: '#a8a3f6' }, // ungu
            { from: '#00cfe8', to: '#6ee3f4' }, // cyan
            { from: '#ff9f43', to: '#ffc178' }, // oranye
            { from: '#ea5455', to: '#f58b8c' }, // merah
            { from: '#28c76f', to: '#7ce3a8' }, // hijau
            { from: '#3b7ddd', to: '#7aa9ec' }, // biru
            { from: '#8b5cf6', to: '#b794f9' }, // violet
            { from: '#f59e0b', to: '#fbc75d' }, // kuning emas
         ]
      },
      barColors() {
         const count = Math.max(this.sortedData.length, 1)

         return Array.from({ length: count }, (_, i) => this.barPalette[i % this.barPalette.length].from)
      },
      barGradientToColors() {
         const count = Math.max(this.sortedData.length, 1)

         return Array.from({ length: count }, (_, i) => this.barPalette[i % this.barPalette.length].to)
      },
      chartSeries() {
         return [{
            name: 'Total',
            data: this.sortedData.map(item => item.total),
         }]
      },
      chartOptions() {
         const textColor = this.isDark ? '#b4b7bd' : '#6e6b7b'

         return {
            chart: {
               type: 'bar',
               foreColor: textColor,
               toolbar: {
                  show: false,
               },
               fontFamily: 'Montserrat, Helvetica, Arial, sans-serif',
            },
            colors: this.barColors,
            plotOptions: {
               bar: {
                  horizontal: true,
                  borderRadius: 4,
                  barHeight: '55%',
                  distributed: true,
               },
            },
            fill: {
               type: 'gradient',
               gradient: {
                  shade: this.isDark ? 'dark' : 'light',
                  type: 'horizontal',
                  shadeIntensity: 0.5,
                  gradientToColors: this.barGradientToColors,
                  inverseColors: false,
                  opacityFrom: 1,
                  opacityTo: 0.9,
                  stops: [0, 100],
               },
            },
            dataLabels: {
               enabled: true,
               textAnchor: 'start',
               offsetX: 0,
               style: {
                  colors: [textColor],
                  fontSize: '12px',
                  fontWeight: 600,
               },
               formatter: value => this.formatCompact(value),
            },
            grid: {
               borderColor: this.isDark ? '#3b4253' : '#ebe9f1',
               strokeDashArray: 4,
            },
            xaxis: {
               min: 0,
               forceNiceScale: true,
               categories: this.sortedData.map(item => item.nama),
               labels: {
                  style: {
                     colors: textColor,
                  },
                  formatter: value => this.formatCompact(value),
               },
               title: {
                  text: 'Total',
                  style: {
                     color: textColor,
                  },
               },
            },
            yaxis: {
               labels: {
                  style: {
                     colors: [textColor],
                  },
                  formatter: value => value,
               },
            },
            tooltip: {
               theme: this.isDark ? 'dark' : 'light',
               y: {
                  formatter: value => `Rp ${value.toLocaleString('id-ID')}`,
               },
            },
         }
      },
   },
   watch: {
      tahun(newYear, oldYear) {
         if (newYear !== oldYear) this.loadData(newYear)
      },
      refreshTimestamp() {
         this.loadData(this.tahun)
      },
   },
   mounted() {
      this.loadData(this.tahun)
   },
   methods: {
      ...mapActions({
         getUmkStatus: 'DashboardLkpm/getUmkStatus',
      }),
      async loadData(year) {
         this.loading = true
         try {
            await this.getUmkStatus(year)
         } catch (error) {
            // Chart tetap menampilkan data kosong saat request gagal.
         } finally {
            this.loading = false
         }
      },
      formatCompact(value) {
         const num = Number(value) || 0
         const abs = Math.abs(num)

         if (abs >= 1e12) return `${(num / 1e12).toLocaleString('id-ID', { maximumFractionDigits: 2 })} T`
         if (abs >= 1e9) return `${(num / 1e9).toLocaleString('id-ID', { maximumFractionDigits: 2 })} M`
         if (abs >= 1e6) return `${(num / 1e6).toLocaleString('id-ID', { maximumFractionDigits: 2 })} Jt`
         if (abs >= 1e3) return `${(num / 1e3).toLocaleString('id-ID', { maximumFractionDigits: 2 })} Rb`

         return num.toLocaleString('id-ID')
      },
   },
}
</script>

<template>
   <b-overlay
      :show="loading"
      :variant="isDark ? 'dark' : 'white'"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
   >
      <b-row>
         <b-col cols="12">
            <b-card
               class="card-wrapper"
               :class="{ 'card-wrapper-dark': isDark }"
            >
               <div class="d-flex justify-content-between align-items-center mb-1">
                  <div>
                     <h4 class="mb-25">
                        Status LKPM UMK Tahun {{ tahun }}
                     </h4>
                     <small class="text-muted">Total Realisasi UMK berdasarkan status</small>
                  </div>
               </div>
               <vue-apex-charts
                  type="bar"
                  height="350"
                  :options="chartOptions"
                  :series="chartSeries"
               />
            </b-card>
         </b-col>
      </b-row>
   </b-overlay>
</template>

<style scoped>
.card-wrapper {
   border: 0;
   border-radius: 12px;
   box-shadow: 0 4px 24px rgba(34, 41, 47, 0.08);
   background-color: #ffffff;
}

.card-wrapper-dark {
   background-color: #283046;
   box-shadow: 0 4px 24px rgba(0, 0, 0, 0.25);
}
</style>
